import { lazy, Suspense, useMemo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { modalRegistry } from "./modalRegistry";
import { uiActions } from "../../../store/ui/ui-slice";
import Icon from "../../../assets/icons/Icon";

const ModalLayout = () => {
  const dispatch = useDispatch();
  const { modalStack } = useSelector((state) => state.ui);
  const stackLength = modalStack?.length;

  const handleModalHide = useCallback(() => {
    dispatch(uiActions.clearModalOption());
  }, [modalStack]);

  const handleCloseAll = useCallback(() => {
    dispatch(uiActions.clearAllModals());
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && stackLength > 0) handleModalHide();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [stackLength, handleModalHide]);

  useEffect(() => {
    if (stackLength > 0) {
      document.body.classList.add("modal-open-custom");
    } else {
      document.body.classList.remove("modal-open-custom");
    }
    return () => document.body.classList.remove("modal-open-custom");
  }, [stackLength]);

  if (stackLength === 0) return null;

  return (
    <div className="custom-modal-stack">
      {stackLength &&
        modalStack.map((modalOptions, index) => (
          <ModalInstance
            key={`${modalOptions.name}-${index}`}
            modalOptions={modalOptions}
            index={index}
            isTop={index === stackLength - 1}
            totalModals={stackLength}
            onClose={handleModalHide}
            onCloseAll={handleCloseAll}
          />
        ))}
    </div>
  );
};

const ModalInstance = ({
  modalOptions,
  index,
  isTop,
  totalModals,
  onClose,
  onCloseAll,
}) => {
  const dispatch = useDispatch();
  const {
    name,
    modalTitle,
    size = "lg",
    showHeader = true,
    backdrop = true,
    rowData,
    payloadData,
  } = modalOptions;

  const modalRef = useRef(null);

  // Auto focus when modal opens
  useEffect(() => {
    if (isTop && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isTop]);

  const ResolvedComponent = useMemo(() => {
    if (!name) return null;
    const entry = modalRegistry[name];
    if (!entry) return null;
    return entry.isLazy ? lazy(entry.component) : entry.component;
  }, [name]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && backdrop !== "static" && isTop) {
      onClose();
    }
  };

  if (!ResolvedComponent) return null;

  const overlayStyle = { zIndex: 1040 + index * 10 };

  const modalStyle = {
    zIndex: 1041 + index * 10,
    ...(isTop
      ? {}
      : {
          transform: `scale(${1 - (totalModals - 1 - index) * 0.05})`,
          opacity: 0.5,
          pointerEvents: "none",
        }),
  };

  const componentProps = {
    dispatch,
    uiActions,
    handleModalHide: onClose,
    handleCloseAll: onCloseAll,
    rowData,
    payloadData,
    fetchData: modalOptions.fetchData,
  };

  return (
    <div
      className="custom-modal-overlay is-visible"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal={isTop}
      style={overlayStyle}
    >
      <div
        className={`custom-modal custom-modal--${size} is-open`}
        style={modalStyle}
        ref={modalRef}
        tabIndex={-1}
      >
        {showHeader && (
          <div className="custom-modal__header">
            {index > 0 && isTop && (
              <button className="custom-modal__back" onClick={onClose}>
                <Icon name="arrow-left" />
              </button>
            )}
            <h5 className="custom-modal__title">{modalTitle}</h5>
            <button className="custom-modal__close" onClick={onClose}>
              <Icon name="times" />
            </button>
          </div>
        )}

        <div className="custom-modal__body">
          {modalRegistry[name]?.isLazy ? (
            <Suspense fallback={<ModalLoader />}>
              <ResolvedComponent {...componentProps} />
            </Suspense>
          ) : (
            <ResolvedComponent {...componentProps} />
          )}
        </div>
      </div>
    </div>
  );
};

const ModalLoader = () => (
  <div className="custom-modal__loader">
    <div className="custom-modal__spinner" />
    <p>Loading...</p>
  </div>
);

export default ModalLayout;
